import React, { useState } from 'react'
import { Check, X } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { textareaClass } from '@/lib/styleKit'
import { cn } from '@/lib/utils'

/**
 * The Approve / Reject pair at the bottom of both review modals.
 *
 * Reject is a two-step action: the first click reveals a required reason box
 * (the Rep sees this text on their rejected workflow / campaign, so it is the
 * whole content of the decision for them); the confirming Reject stays
 * disabled until the reason has non-blank text. Styling is firm but not
 * alarming (brief section 7: "a clear but non-harsh treatment").
 */
interface Props {
  approveLabel?: string
  rejectLabel?: string
  onApprove: () => Promise<void> | void
  onReject: (reason: string) => Promise<void> | void
  busy?: boolean
  /** Hides both actions (e.g. the request is no longer pending). */
  disabled?: boolean
}

export const REASON_PLACEHOLDER = 'Tell the requester what to fix, e.g. "Subject line is off-brand, use the Q4 messaging."'

const DecisionFooter: React.FC<Props> = ({
  approveLabel = 'Approve',
  rejectLabel = 'Reject',
  onApprove,
  onReject,
  busy = false,
  disabled = false,
}) => {
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const canSubmitReject = reason.trim().length > 0 && !busy

  const submitReject = async () => {
    if (!canSubmitReject) return
    await onReject(reason.trim())
  }

  return (
    <div className="space-y-3">
      {rejecting && (
        <div className="space-y-1.5">
          <label
            htmlFor="decision-reject-reason"
            className="block text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]"
          >
            Reason for rejecting (required)
          </label>
          <textarea
            id="decision-reject-reason"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={REASON_PLACEHOLDER}
            rows={3}
            autoFocus
            className={cn(textareaClass, 'resize-none')}
            style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
          />
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        {rejecting ? (
          <>
            <Button variant="ghost" size="sm" onClick={() => { setRejecting(false); setReason('') }} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={submitReject}
              disabled={!canSubmitReject || disabled}
              loading={busy}
              icon={<X className="h-3.5 w-3.5 text-destructive" />}
              className="border-red-500/30 text-red-700 hover:bg-red-500/[0.06] dark:text-red-300"
            >
              {rejectLabel}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRejecting(true)}
              disabled={busy || disabled}
              icon={<X className="h-3.5 w-3.5 text-destructive" />}
            >
              {rejectLabel}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => onApprove()}
              disabled={disabled}
              loading={busy}
              icon={<Check className="h-3.5 w-3.5" />}
            >
              {approveLabel}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default DecisionFooter
